"use client";

import { useEffect, useState } from "react";
import { IoClose } from "react-icons/io5";
import noteAPI from "../services/note/noteAPI";
import StatusPopUp from "./popup/statusPopUp";
import { useTranslations } from "next-intl";

type NoteTarget = {
    gameId: number | null;
    source: string;
    title: string;
};

type NotePopUpProps = {
    target: NoteTarget;
    onClose: () => void;
    onCreated?: () => void;
    mode?: "create" | "update";
    initialNote?: {
        id?: number | string;
        name?: string;
        detail?: string;
        create_at?: string;
    } | null;
    onSaved?: (
        note: {
            id: number | string;
            name: string;
            detail: string;
            create_at: string;
        },
        mode: "create" | "update",
    ) => void;
};

export default function NotePopUp({ target, onClose, onCreated, mode = "create", initialNote = null, onSaved }: NotePopUpProps) {
    const [name, setName] = useState("");
    const [detail, setDetail] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [statusPopUp, setStatusPopUp] = useState<{
        title: string;
        message: string;
        type: "info" | "success" | "error";
    } | null>(null);

    const tNote = useTranslations("note");

    // const dateLabel = useMemo(() => getCompactDateTimeLabel(new Date()), []);

    useEffect(() => {
        setName(String(initialNote?.name ?? ""));
        setDetail(String(initialNote?.detail ?? ""));
    }, [initialNote?.detail, initialNote?.name]);

    useEffect(() => {
        const prevHtmlOverflow = document.documentElement.style.overflow;
        const prevBodyOverflow = document.body.style.overflow;

        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";

        return () => {
            document.documentElement.style.overflow = prevHtmlOverflow;
            document.body.style.overflow = prevBodyOverflow;
        };
    }, []);

    const handleSave = async () => {
        const trimmedName = name.trim();
        const trimmedDetail = detail.trim();
        let teacherUuid: string | undefined;

        try {
            const rawUser = localStorage.getItem("user");
            if (rawUser) {
                const parsedUser = JSON.parse(rawUser) as { id?: unknown };
                if (parsedUser?.id !== undefined && parsedUser.id !== null && String(parsedUser.id).trim() !== "") {
                    teacherUuid = String(parsedUser.id);
                }
            }
        } catch {
            teacherUuid = undefined;
        }

        if (!trimmedName) {
            setStatusPopUp({
                title: tNote("alert"),
                message: tNote("emptyTitle"),
                type: "info",
            });
            return;
        }

        if (!target.gameId) {
            setStatusPopUp({
                title: tNote("failed"),
                message: tNote("missingGameId"),
                type: "error",
            });
            return;
        }

        const source = String(target.source ?? "").toLowerCase();

        if (mode === "update" && !initialNote?.id) {
            setStatusPopUp({
                title: tNote("failed"),
                message: tNote("missingNoteId"),
                type: "error",
            });
            return;
        }

        setIsSaving(true);

        const response = mode === "update"
            ? await noteAPI.update(String(initialNote?.id ?? ""), {
                name: trimmedName,
                detail: trimmedDetail,
                ...(source === "sequence"
                    ? { id_sequenceinfo: target.gameId }
                    : { id_gameinfo: target.gameId }),
            })
            : await noteAPI.create({
                name: trimmedName,
                detail: trimmedDetail,
                ...(teacherUuid ? { teacher_uuid: teacherUuid } : {}),
                ...(source === "sequence"
                    ? { id_sequenceinfo: target.gameId }
                    : { id_gameinfo: target.gameId }),
            });

        setIsSaving(false);

        if (!response || (response as { status?: string }).status === "failed") {
            setStatusPopUp({
                title: tNote("failed"),
                message: mode === "update"
                    ? tNote("updateFailed")
                    : tNote("createFailed"),
                type: "error",
            });
            return;
        }

        const responseRecord = response as Record<string, unknown>;
        const dataRecord = typeof responseRecord.data === "object" && responseRecord.data
            ? (responseRecord.data as Record<string, unknown>)
            : responseRecord;
        const savedId = mode === "update"
            ? initialNote?.id
            : dataRecord.id ?? dataRecord.note_id ?? dataRecord.id_note ?? `${Date.now()}`;

        const normalizedSavedId = typeof savedId === "number" || typeof savedId === "string"
            ? savedId
            : String(savedId ?? "");
        const savedCreateAt = String(
            dataRecord.create_at
            ?? dataRecord.createdAt
            ?? initialNote?.create_at
            ?? new Date().toISOString(),
        );

        if (normalizedSavedId !== "") {
            onSaved?.(
                {
                    id: normalizedSavedId,
                    name: trimmedName,
                    detail: trimmedDetail,
                    create_at: savedCreateAt,
                },
                mode,
            );
        }

        setStatusPopUp({
            title: tNote("success"),
            message: mode === "update" ? tNote("updateSuccess") : tNote("createSuccess"),
            type: "success",
        });
        onCreated?.();
    };

    const handleCloseStatus = () => {
        const isSuccess = statusPopUp?.type === "success";
        setStatusPopUp(null);
        if (isSuccess) {
            onClose();
        }
    };

    return (
        <>
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
                <div className="w-full max-w-85 rounded-md bg-[#F4F4F4] px-4 pb-6 pt-4 shadow-[0_6px_20px_rgba(0,0,0,0.35)]">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-bold text-[#5A5A5A]">
                            {mode === "update" ? tNote("updateNote") : tNote("createNote")}
                        </h2>
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-[#d5d9dd] text-[#8e949b] cursor-pointer hover:bg-[#d5d9dd]/90"
                        >
                            <IoClose size={20} />
                        </button>
                    </div>

                    <div className="mt-2 flex items-center justify-between">
                        <label className="mb-0.5 text-lg font-medium leading-none text-[#5A5A5A]">{tNote("title")}</label>
                        {/* <span className="text-base text-[#8d8d8d]">{dateLabel}</span> */}
                    </div>

                    <input
                        type="text"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        className="mt-1 h-12 w-full rounded border border-[#dedede] bg-white px-3 text-lg text-[#4E4E4E] outline-none"
                    />

                    <label className="mt-5 mb-0.5 block text-lg font-medium leading-none text-[#5A5A5A]">{tNote("detail")}</label>
                    <textarea
                        value={detail}
                        onChange={(event) => setDetail(event.target.value)}
                        className="mt-1 h-48 w-full resize-none rounded border border-[#dedede] bg-white p-3 text-lg text-[#4E4E4E] outline-none"
                    />

                    <div className="mt-6 flex items-center justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSaving}
                            className="min-w-20 rounded-md px-6 py-2 text-base font-semibold text-[#0C89CF] disabled:opacity-70 cursor-pointer"
                        >
                            {tNote("cancel")}
                        </button>
                        <button
                            type="button"
                            onClick={() => void handleSave()}
                            disabled={isSaving}
                            className="min-w-28 rounded-md bg-[#1094d4] px-10 py-2 text-base font-semibold text-white disabled:opacity-70 cursor-pointer hover:bg-[#1094d4]/90"
                        >
                            {isSaving ? "..." : mode === "update" ? tNote("saveChanges") : tNote("save")}
                        </button>
                    </div>
                </div>
            </div>

            {statusPopUp ? (
                <StatusPopUp
                    title={statusPopUp.title}
                    message={statusPopUp.message}
                    type={statusPopUp.type}
                    onClose={handleCloseStatus}
                />
            ) : null}
        </>
    );
}
